import { useEffect } from "react";

import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import Textarea from "@/common/components/form/Textarea.tsx";
import { banAgency, banGroup } from "@/modules/profile/services";

import { ManagementType } from "../modules/Management/types/commonTypes";

type FormBan = {
  reasonDisabled: string;
  isDisabled: boolean;
};

const ModalBan = ({
  data,
  id,
  show,
  onHide,
}: {
  id: string;
  data: {
    is_disabled: boolean;
    reason_disabled: null | string;
    level: string;
    type: ManagementType.AGENCY | ManagementType.GROUP;
    location: string;
    permissions: { show: boolean; update: boolean; delete: boolean };
  };
  show: boolean;
  onHide: () => void;
}) => {
  const type = data?.type;
  const modalTitle =
    type === ManagementType.AGENCY
      ? `Agence : ${data?.level ?? ""}`
      : `Groupe : ${data?.level ?? ""}`;

  const methods = useForm<FormBan>();

  useEffect(() => {
    methods.reset({
      reasonDisabled: data?.reason_disabled ?? "",
      isDisabled: data?.is_disabled,
    });
  }, [data, methods]);

  const queryClient = useQueryClient();

  const mutationBan = useMutation<
    any,
    Error,
    FormBan & {
      isOnlyUpdateReason: boolean;
    }
  >({
    mutationFn: async (data) => {
      const isDisabled = data.isOnlyUpdateReason
        ? data.isDisabled
        : !data.isDisabled;
      const reasonDisabled = isDisabled ? data.reasonDisabled : "";
      switch (type) {
        case ManagementType.AGENCY:
          return await banAgency(id, {
            isDisabled,
            reasonDisabled,
          });
        case ManagementType.GROUP:
          return await banGroup(id, {
            isDisabled,
            reasonDisabled,
          });
      }
    },
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors du blocage");
    },
    onSuccess: async () => {
      toast.success(modalTitle + ", modifié avec succès");
      await invalidateData();
      handleClose();
    },
  });

  const invalidateData = async () => {
    await queryClient.invalidateQueries({ queryKey: ["getAllGroups"] });
  };

  const handleClose = () => {
    methods.reset();
    onHide();
  };

  const handleSubmit = (isOnlyUpdateReason: boolean) => (data: FormBan) =>
    mutationBan.mutate({
      ...data,
      isOnlyUpdateReason,
    });

  return (
    <>
      <Modal size="lg" show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="fw-bold">&nbsp;{modalTitle}</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="d-flex flex-column gap-3 p-5">
          <Textarea<FormBan>
            control={methods.control}
            name={"reasonDisabled"}
            label={"Décrire la raison du blocage"}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Retour
          </Button>
          {data?.is_disabled ? (
            <Button
              variant={"secondary"}
              onClick={methods.handleSubmit(handleSubmit(true))}
              form={"UserForm"}
              className={"ml-2 text-white d-flex align-items-center"}
            >
              <span>Modifier</span>
            </Button>
          ) : null}

          <Button
            variant={data?.is_disabled ? "success" : "warning"}
            onClick={methods.handleSubmit(handleSubmit(false))}
            form={"UserForm"}
            className={"ml-2 text-white d-flex align-items-center"}
          >
            {data?.is_disabled ? (
              <>
                <i className="pi pi-check me-2" />
                <span>Débloquer</span>
              </>
            ) : (
              <>
                <i className="pi pi-ban me-2" />
                <span>Bloquer</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModalBan;
